import React, { useEffect, useMemo, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import { EditionAwarenessUpsell as EditionAwarenessUpsellPlatform } from '@atlassian/edition-awareness/ui/edition-awareness-upsell';

import { fg } from '@confluence/feature-gating';
import { useSiteTraits } from '@confluence/targeting/entry-points/useSiteTraits.ts';

import { useConfluenceStandardToPremiumExperiment } from './useConfluenceStandardToPremiumExperiment.tsx';

type Trait = {
	name: string;
	value: boolean | string | null;
};

// Copied from Platform EA - temporary usage for ea_s2p_refactor_verify_trait_eligibility
const parseBooleanTrait = (trait: Trait | undefined): boolean => {
	if (!trait) {
		return false;
	}
	if (typeof trait.value === 'boolean') {
		return trait.value;
	}
	if (typeof trait.value === 'string') {
		return trait.value === 'true';
	}
	return false;
};

export const EditionAwarenessUpsell = ({
	navVersion,
}: {
	navVersion?: 'nav3' | 'nav4';
}): React.JSX.Element | null => {
	const { isEligibleForUpsellExperiment, ...rest } = useConfluenceStandardToPremiumExperiment();

	// START ea_s2p_refactor_verify_trait_eligibility
	const siteTraitsResult = useSiteTraits({
		skipBootstrap: !fg('ea_s2p_refactor_verify_trait_eligibility'),
	});
	const siteTraits = useMemo(() => siteTraitsResult.data?.attributes ?? [], [siteTraitsResult]);
	const hasFiredTraitAnalytics = useRef(!fg('ea_s2p_refactor_verify_trait_eligibility'));
	const { createAnalyticsEvent } = useAnalyticsEvents();

	useEffect(() => {
		if (
			!hasFiredTraitAnalytics.current &&
			!siteTraitsResult.loading &&
			rest.edition === 'standard' &&
			rest.userRoles?.includes('SITE_ADMIN')
		) {
			hasFiredTraitAnalytics.current = true;
			const isEligibleForConfluenceStandardToPremium = parseBooleanTrait(
				siteTraits.find(
					(trait) => trait.name === 'ea_confluence_is_eligible_for_standard_to_premium',
				),
			);
			createAnalyticsEvent({
				type: 'sendTrackEvent',
				data: {
					action: 'checked',
					actionSubject: 'eligibility',
					source: 'EditionAwarenessUpsell',
					attributes: {
						featureName: 'editionAwarenessStandardToPremiumUpsell',
						product: rest.product,
						edition: rest.edition,
						isDismissed: rest.hasDismissedButton,
						isEligibleForUpsell: isEligibleForUpsellExperiment,
						isEligibleForConfluenceStandardToPremium,
					},
				},
			}).fire();
		}
	}, [
		rest.edition,
		rest.userRoles,
		siteTraitsResult.loading,
		siteTraits,
		createAnalyticsEvent,
		rest.product,
		isEligibleForUpsellExperiment,
		rest.hasDismissedButton,
	]);
	// END ea_s2p_refactor_verify_trait_eligibility

	if (isEligibleForUpsellExperiment) {
		return <EditionAwarenessUpsellPlatform navVersion={navVersion} {...rest} />;
	}

	return null;
};
